import { Button } from "../../ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/form";
import { Input } from "../../ui/input";
import { Alert, AlertTitle } from "../../ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { OpenAIChatSettings } from "@/types/ChatSettings";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { openAIChatProviderValidator } from "@shared/validators/admin/providers/chat-provider-validator";
import type { ZodType } from "zod";

type Props = {
    data?: OpenAIChatSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const OpenAIChatSettingsForm = forwardRef(
    (
        { data, initialData = {}, onDataChange, submitButton, readOnly, hideSubmitButton, error, onSubmit }: Props,
        ref
    ) => {
        const form = useForm<OpenAIChatSettings>({
            resolver: zodResolver(openAIChatProviderValidator as ZodType<OpenAIChatSettings>),
            defaultValues: {
                OPENAI_API_KEY: initialData?.OPENAI_API_KEY || data?.OPENAI_API_KEY || "",
                OPENAI_MODEL: initialData?.OPENAI_MODEL || data?.OPENAI_MODEL || "gpt-4o",
                OPENAI_BASE_URL: initialData?.OPENAI_BASE_URL || data?.OPENAI_BASE_URL || "https://api.openai.com/v1",
                OPENAI_TEMPERATURE: initialData?.OPENAI_TEMPERATURE || data?.OPENAI_TEMPERATURE || 0.7,
                OPENAI_MAX_TOKENS: initialData?.OPENAI_MAX_TOKENS || data?.OPENAI_MAX_TOKENS || 4096,
            },
        });

        useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });
            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("OpenAI Chat validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => form.getValues(),
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => onSubmit && onSubmit(data))}>
                    <div>
                        {error && (
                            <Alert variant="destructive" className="mb-2 dark:bg-red-800 dark:text-white">
                                <div className="flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4" />
                                    <AlertTitle className="mb-0">{error}</AlertTitle>
                                </div>
                            </Alert>
                        )}
                        <div className="space-y-3">
                            <FormField
                                control={form.control}
                                name="OPENAI_API_KEY"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>API Key</FormLabel>
                                        <FormControl>
                                            <Input 
                                                {...field} 
                                                type="password" 
                                                readOnly={readOnly}
                                                placeholder="sk-..."
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="OPENAI_MODEL"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Model</FormLabel>
                                        <FormControl>
                                            <Input 
                                                {...field} 
                                                readOnly={readOnly}
                                                placeholder="e.g., gpt-4o, gpt-4o-mini, gpt-4-turbo"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="OPENAI_BASE_URL"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Base URL (optional)</FormLabel>
                                        <FormControl>
                                            <Input 
                                                {...field} 
                                                readOnly={readOnly}
                                                placeholder="https://api.openai.com/v1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    {submitButton ? (
                        submitButton
                    ) : hideSubmitButton ? null : (
                        <div className="flex justify-end pt-10">
                            <Button type="submit">Save Changes</Button>
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

OpenAIChatSettingsForm.displayName = "OpenAIChatSettingsForm";

export default OpenAIChatSettingsForm;

